import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import TcnApp from '../../components/tcn/TcnApp';
import TopBar from '../../components/tcn/layout/TopBar';
import { Card, Avatar, AccountAvatar, Icon, SectionLabel, CTAButton } from '../../components/tcn/ui-kit';
import { TCN } from '../../components/tcn/ui-kit/tokens';
import AlertBell from '../../components/tcn-modules/m7/AlertBell';
import HeaderStrip from '../../components/tcn-modules/m1/HeaderStrip';
import StrategicFocusBanner from '../../components/tcn-modules/m1/StrategicFocusBanner';
import OverviewMetricCard from '../../components/tcn-modules/m1/OverviewMetricCard';
import RosterQualityCard from '../../components/tcn-modules/m1/RosterQualityCard';
import NewCreatorHealthCard from '../../components/tcn-modules/m1/NewCreatorHealthCard';
import PaceFlagsCard from '../../components/tcn-modules/m1/PaceFlagsCard';
import OtherGapsCard from '../../components/tcn-modules/m1/OtherGapsCard';
import CompareLastMonthCard from '../../components/tcn-modules/m1/CompareLastMonthCard';
import {
  formatInt,
  formatDubaiDayLine,
  dubaiCycleDay,
  dubaiDayKey,
  dubaiHour,
} from '../../components/tcn-modules/shared/format';
import useIsDesktop from '../../hooks/useIsDesktop';

/**
 * /tcn/me — Module 1 (My Performance) dashboard.
 *
 * Single SELECT on `manager_current_month` filtered by manager_id = auth.uid().
 * RLS enforces the filter server-side; for non-manager users the row simply
 * won't exist and we render the empty state.
 *
 * Layout — single scroll, no tabs (mirrors the mockup, which stacks every A1.x
 * artboard one after another on one page):
 *   - HeaderStrip (4 totals)
 *   - StrategicFocusBanner (biggest_gap_metric)         — A1.1 / A1.7
 *   - Overview metrics: Diamonds + Recruited            — A1.2
 *   - PaceFlagsCard (below-pace flags)                  — A1.3
 *   - Roster Quality + New Creator Health               — A1.4 / A1.5
 *   - CompareLastMonthCard (trends vs LM)               — A1.6
 *   - OtherGapsCard (ranked remaining gaps)             — A1.7
 *   - Departures footer
 */
const TcnMeIndex = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const isDesktop = useIsDesktop();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Briefing redirect gate — first time today, send to /briefing.
  useEffect(() => {
    if (!user?.id) return;
    const todayKey = new Date()
      .toLocaleDateString('sv-SE', { timeZone: 'Asia/Dubai' })
      .replace(/-/g, '');
    const seenKey = `tcn_briefing_seen_${user.id}_${todayKey}`;
    if (typeof window !== 'undefined' && !window.localStorage.getItem(seenKey)) {
      navigate('/tcn/me/briefing', { replace: true });
    }
  }, [user?.id, navigate]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    // Manager dashboard row — `*_clear` columns now populated by recompute (Phase A).
    const { data, error } = await supabase
      .from('manager_current_month')
      .select('*')
      .eq('manager_id', user.id)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') {
      setError(error);
    } else {
      setData(data || null);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  // A1.3 — today's top priority flag (PaceFlagsCard write target,
  // OverviewMetricCard read consumer). State lifted up here so a toggle on the
  // Pace card immediately re-renders the matching Diamonds / Recruited overview
  // card with a "★ PRIORITY" pill, without re-reading the DB. Persisted in
  // tcn_manager_daily_priority — RLS manager_id = auth.uid().
  const todayDayKey = dubaiDayKey();
  const [priorityKey, setPriorityKey] = useState(null);
  const [priorityLoading, setPriorityLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    (async () => {
      const { data: row } = await supabase
        .from('tcn_manager_daily_priority')
        .select('metric_key')
        .eq('manager_id', user.id)
        .eq('day_key', todayDayKey)
        .maybeSingle();
      if (!cancelled) setPriorityKey(row?.metric_key ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, todayDayKey]);

  const handleTogglePriority = useCallback(
    async (metricKey) => {
      if (!user?.id || priorityLoading) return;
      const previous = priorityKey;
      const next = priorityKey === metricKey ? null : metricKey;
      setPriorityLoading(true);
      setPriorityKey(next); // optimistic
      // Supabase JS does NOT throw on RLS / constraint errors — it returns
      // them in `error`. Skipping the check would leave the optimistic UI in
      // place while the DB row is unchanged → on reload, state silently
      // reverts and the user thinks "rien ne se passe".
      const op =
        next === null
          ? supabase
              .from('tcn_manager_daily_priority')
              .delete()
              .eq('manager_id', user.id)
              .eq('day_key', todayDayKey)
          : supabase
              .from('tcn_manager_daily_priority')
              .upsert(
                { manager_id: user.id, day_key: todayDayKey, metric_key: next },
                { onConflict: 'manager_id,day_key' }
              );
      const { error: dbErr } = await op;
      if (dbErr) {
        // Revert + surface — keeps the UI honest.
        console.error('[tcn_manager_daily_priority] toggle failed:', dbErr);
        setPriorityKey(previous);
      }
      setPriorityLoading(false);
    },
    [user?.id, todayDayKey, priorityKey, priorityLoading]
  );

  const handleTabChange = (tab) => {
    switch (tab) {
      case 'today':    return navigate('/tcn/me');
      case 'creators': return navigate('/tcn/me/creators');
      case 'tiers':    return navigate('/tcn/me/tiers');
      case 'payout':   return navigate('/tcn/me/payout');
      case 'actions':  return navigate('/tcn/me/actions');
      default:         return navigate('/tcn/me');
    }
  };

  const userName = userProfile?.full_name || userProfile?.display_name || userProfile?.email || 'Agent';

  // Greeting header (mirrors mockup A1.1): time-based greeting + first name,
  // subtitle = localized weekday/date + payment-cycle counter ("Day 14 of 30").
  const now = new Date();
  const firstName = userName.trim().split(/\s+/)[0];
  const hour = dubaiHour(now);
  const greetingKey = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const greeting = t(`tcn.m1.greeting.${greetingKey}`, {
    name: firstName,
    defaultValue:
      greetingKey === 'morning'
        ? 'Good morning, {{name}}'
        : greetingKey === 'afternoon'
        ? 'Good afternoon, {{name}}'
        : 'Good evening, {{name}}',
  });
  const dateLocale = t('tcn.briefing.dateLocale', 'en-AE');
  const { day: cycleDay, total: cycleTotal } = dubaiCycleDay(now);
  const subtitleText = `${formatDubaiDayLine(now, dateLocale)} · ${t('tcn.m1.cycleDay', {
    day: cycleDay,
    total: cycleTotal,
    defaultValue: 'Day {{day}} of {{total}}',
  })}`;

  return (
    <TcnApp
      tab="today"
      onTabChange={handleTabChange}
      desktopUserName={userName}
      desktopBreadcrumb={[t('tcn.m1.breadcrumb.dashboard', 'Dashboard'), t('tcn.tabs.today', 'Today')]}
      mobileHeader={
        <TopBar
          leading={<AccountAvatar name={userName} size={36} ring online />}
          trailing={
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                type="button"
                onClick={() => navigate('/tcn/me/creators')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: TCN.textDim,
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label={t('tcn.m4.searchPh', 'Search creator…')}
              >
                <Icon name="search" size={20} />
              </button>
              <button
                type="button"
                onClick={() => navigate('/tcn/me/briefing?force=1')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: TCN.cyan,
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label="Briefing"
              >
                <Icon name="sparkle" size={20} />
              </button>
              <AlertBell />
            </div>
          }
          title={greeting}
          subtitle={subtitleText}
          largeTitle
        />
      }
    >
      <div
        style={{
          padding: isDesktop ? 0 : 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          maxWidth: isDesktop ? 1200 : '100%',
        }}
      >
        {loading ? (
          <SkeletonView />
        ) : error ? (
          <ErrorView error={error} onRetry={load} />
        ) : !data ? (
          <EmptyView userName={userName} />
        ) : (
          <>
            {/* 1. Header strip (portfolio totals — present on every artboard) */}
            <HeaderStrip data={data} isDesktop={isDesktop} />

            {/* A1.7 — today's strategic focus (biggest gap) + ranked other gaps */}
            <StrategicFocusBanner data={data} isDesktop={isDesktop} />
            <OtherGapsCard data={data} excludeMetric={data.biggest_gap_metric} />

            {/* A1.2 — Overview metrics (Diamonds + Recruited) */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr',
                gap: 12,
              }}
            >
              <OverviewMetricCard
                title={t('tcn.m1.overview.diamondsTitle', 'Diamonds')}
                kind="diamonds"
                data={data}
                accent="diamond"
                isPriority={priorityKey === 'diamonds'}
              />
              <OverviewMetricCard
                title={t('tcn.m1.overview.recruitedTitle', 'Recruited creators')}
                kind="recruited"
                data={data}
                accent="cyan"
                isPriority={priorityKey === 'recruited'}
              />
            </div>

            {/* A1.3 — below-pace flags */}
            <SectionHeader
              title={t('tcn.m1.pace.title', "Today's pace")}
              subtitle={t('tcn.m1.pace.subtitle', 'Below-pace metrics flagged')}
            />
            <PaceFlagsCard
              data={data}
              priorityKey={priorityKey}
              onTogglePriority={handleTogglePriority}
              priorityLoading={priorityLoading}
            />

            {/* A1.4 — roster quality */}
            <SectionHeader
              title={t('tcn.m1.roster.title', 'Roster quality')}
              subtitle={t('tcn.m1.roster.subtitle', 'Key metrics for your active roster')}
            />
            <RosterQualityCard data={data} />

            {/* A1.5 — new creator health */}
            <SectionHeader
              title={t('tcn.m1.newCreator.title', 'New creator health')}
              subtitle={t('tcn.m1.newCreator.subtitle', 'First 30-day quality metrics')}
            />
            <NewCreatorHealthCard data={data} />

            {/* A1.6 — trends vs last month */}
            <SectionHeader
              title={t('tcn.m1.trends.title', 'Trends')}
              subtitle={t('tcn.m1.trends.subtitle', 'vs Last Month · Growth target')}
            />
            <CompareLastMonthCard data={data} />

            {/* Departures footer */}
            {(data.departed_creators_mtd > 0 || data.departed_creators_total > 0) && (
              <Card>
                <SectionLabel>{t('tcn.m1.departures.title', 'Departures')}</SectionLabel>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    color: TCN.textDim,
                    marginTop: 8,
                  }}
                >
                  <span>
                    {t('tcn.m1.departures.mtd', 'This month')}:{' '}
                    <strong style={{ color: TCN.text }}>
                      {formatInt(data.departed_creators_mtd)}
                    </strong>
                  </span>
                  <span>
                    {t('tcn.m1.departures.total', 'Total')}:{' '}
                    <strong style={{ color: TCN.text }}>
                      {formatInt(data.departed_creators_total)}
                    </strong>
                  </span>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </TcnApp>
  );
};

// iOS-style section header (large title + subtitle) used between stacked
// artboards on the single-scroll dashboard — mirrors each mockup TopBar.
const SectionHeader = ({ title, subtitle }) => (
  <div>
    <div
      style={{
        fontSize: 20,
        fontWeight: 700,
        color: TCN.text,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
      }}
    >
      {title}
    </div>
    {subtitle && (
      <div style={{ fontSize: 13, color: TCN.textDim, marginTop: 2 }}>{subtitle}</div>
    )}
  </div>
);

const SkeletonView = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        style={{
          height: i === 1 ? 100 : 160,
          borderRadius: 16,
          background: TCN.surface1,
          border: `1px solid ${TCN.border}`,
        }}
        className="animate-pulse"
      />
    ))}
  </div>
);

const ErrorView = ({ error, onRetry }) => (
  <Card accent="red">
    <div style={{ textAlign: 'center', padding: 14 }}>
      <Icon name="warn" size={28} color={TCN.red} />
      <div style={{ marginTop: 10, fontSize: 14, color: TCN.text, fontWeight: 600 }}>
        {error?.message || 'Could not load your dashboard'}
      </div>
      <div style={{ marginTop: 12, width: 'auto', display: 'inline-block' }}>
        <CTAButton variant="primary" size="sm" onClick={onRetry} fullWidth={false}>
          Retry
        </CTAButton>
      </div>
    </div>
  </Card>
);

const EmptyView = ({ userName }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <div style={{ textAlign: 'center', padding: 28 }}>
        <Avatar name={userName} size={56} />
        <div style={{ marginTop: 14, fontSize: 18, fontWeight: 700, color: TCN.text }}>
          {t('tcn.m1.empty.title', 'No data yet')}
        </div>
        <div style={{ marginTop: 6, fontSize: 13, color: TCN.textDim }}>
          {t(
            'tcn.m1.empty.body',
            'Once your agency uploads creator data, your dashboard will populate here.'
          )}
        </div>
      </div>
    </Card>
  );
};

export default TcnMeIndex;
